import { useEffect, useState } from "react"

// ** Third Party Components
import { Loader } from "lucide-react"
import { useLocation, useNavigate } from "react-router-dom"
import { ArrowDown2, ArrowRight2, ArrowSquareDown, ArrowSquareRight } from "iconsax-reactjs"

// ** helpers
import { cn } from "@pma/utils/helpers"

// ** Store
import { useCommonStore } from "@pma/stores/common-store"

// **Types
import { NavGroupProps } from "@pma/types"

// ** Custom Hooks
import useTranslator from "@pma/hooks/use-translator"

// ** Custom Components
import NavBarLink from "./NavBarLink"

// Recursively check if any descendant matches the given pathname
const hasActiveDescendant = (items: any[], pathname: string): boolean =>
  items.some(
    (c) =>
      (c.path && pathname === c.path) || (c.children && hasActiveDescendant(c.children, pathname)),
  )

const NavGroup = ({
  title,
  icon,
  path,
  item,
  parent,
  children,
  group_id,
  loadingGroupId,
  siblingPaths,
}: NavGroupProps) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { locale, translate } = useTranslator()
  const { setHeaderBreadcrumbsListAction } = useCommonStore()

  const isHebrew = locale === "he"

  const [open, setOpen] = useState(parent && !item?.defaultCollapsed)

  const isPathInHierarchy = (): boolean => {
    if (!path) return false
    if (location.pathname === path) return false
    if (!location.pathname.startsWith(path + "/")) return false
    if (siblingPaths?.some((sp) => sp !== path && location.pathname.startsWith(sp))) return false
    return true
  }

  useEffect(() => {
    if (parent) return
    if (hasActiveDescendant(children || [], location.pathname) || isPathInHierarchy()) {
      setOpen(true)
    }
  }, [parent, children, location.pathname])

  const isLoading = group_id !== undefined && group_id === loadingGroupId

  const isActive =
    (!!path && location.pathname === path) || hasActiveDescendant(children || [], location.pathname)

  const handleClick = () => {
    setOpen((prev) => !prev)

    if (item.breadcrumbs) {
      localStorage.setItem("breadcrumbs", JSON.stringify(item.breadcrumbs))
      setHeaderBreadcrumbsListAction(item.breadcrumbs)
    }

    if (path && location.pathname !== path) {
      navigate(path)
    }
  }

  return (
    <div className='space-y-1'>
      <button
        onClick={handleClick}
        className={cn(
          "flex w-full items-center gap-2 rounded-lg px-3 py-2 font-[inter] text-sm text-neutral-700 hover:bg-indigo-200",
          isActive && "bg-indigo-200 font-bold",
        )}
      >
        {icon ? icon : null}

        {!parent ? (
          <span>
            {isLoading ? (
              <Loader className='h-4 w-4 animate-spin' />
            ) : open ? (
              <ArrowDown2 size='16' color='#64748B' variant='Bold' />
            ) : (
              <ArrowRight2 size='16' color='#64748B' variant='Bold' />
            )}
          </span>
        ) : null}

        {translate(title)}

        {parent ? (
          <span className={cn(isHebrew ? "mr-auto" : "ml-auto")}>
            {group_id == null ? (
              open ? (
                <ArrowSquareDown size='20' color='#C7D7FE' variant='Bold' />
              ) : (
                <ArrowSquareRight size='20' color='#C7D7FE' variant='Bold' />
              )
            ) : isLoading ? (
              <Loader className='ml-2 h-4 w-4 animate-spin' />
            ) : open ? (
              <ArrowDown2 size='16' color='#000' />
            ) : (
              <div className='rounded bg-indigo-200 p-[1px]'>
                <ArrowRight2 size='16' color='#000000' />
              </div>
            )}
          </span>
        ) : null}
      </button>

      {open && children && !isLoading && (
        <div className={cn("space-y-1", isHebrew ? "mr-5" : "ml-5")}>
          {children.map((child) =>
            child.children ? (
              <NavGroup
                key={child.path || child.title}
                title={child.title}
                icon={child.icon}
                parent={false}
                item={child}
                path={child.path}
                group_id={child.group_id}
                children={child.children}
                loadingGroupId={loadingGroupId}
              />
            ) : (
              <NavBarLink
                key={child.path || child.title}
                title={child.title}
                icon={child.icon}
                item={child}
                path={child.path}
                disabled={child.disabled}
                externalLink={child.externalLink}
                openInNewTab={child.openInNewTab}
              />
            ),
          )}
        </div>
      )}
    </div>
  )
}

export default NavGroup
